import React from 'react';
import {
  Editor, InputRule, NodeViewContent, NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer,
} from '@tiptap/react';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { BlockMath, InlineMath } from '@tiptap/extension-mathematics';
import { CODE_LANGUAGES, KATEX_OPTIONS, lowlight } from '../utils/noteRichContent';
import { useLanguage } from '../i18n/LanguageContext';

// A code block with a language menu in its corner. The third backtick opens
// the block straight away, so there is no chance to type ```latex first; the
// menu is where the language gets chosen, and it is what decides the colours.
const CodeBlockView: React.FC<NodeViewProps> = ({ node, updateAttributes }) => {
  // TipTap renders the node view through a React portal into the tree the
  // editor lives in, so the language context is still above it.
  const { t } = useLanguage();
  return (
    <NodeViewWrapper className="code-block">
      <select
        contentEditable={false}
        className="code-block-language"
        value={node.attrs.language || ''}
        onChange={(event) => updateAttributes({ language: event.target.value || null })}
        aria-label={t.addNote.codeLanguage}
      >
        {CODE_LANGUAGES.map((lang) => (
          <option key={lang.value} value={lang.value}>{lang.label}</option>
        ))}
      </select>
      <pre>
        <NodeViewContent<'code'> as="code" />
      </pre>
    </NodeViewWrapper>
  );
};

export const NoteCodeBlock = CodeBlockLowlight.extend({
  addNodeView() {
    return ReactNodeViewRenderer(CodeBlockView);
  },
}).configure({ lowlight });

// Equations are edited by clicking them. A prompt is plain, but it shows the
// whole source at once, which is what you want when fixing a brace.
// The prompt's wording is passed in: this is a plain helper, so it cannot
// reach the language context itself.
const editLatex = (
  editor: Editor,
  type: 'inlineMath' | 'blockMath',
  latex: string,
  pos: number,
  promptLabel: string,
) => {
  const next = window.prompt(promptLabel, latex);
  if (next === null) return;
  const chain = editor.chain().focus();
  if (type === 'inlineMath') {
    (next.trim() ? chain.updateInlineMath({ latex: next, pos }) : chain.deleteInlineMath({ pos })).run();
  } else {
    (next.trim() ? chain.updateBlockMath({ latex: next, pos }) : chain.deleteBlockMath({ pos })).run();
  }
};

// The usual Markdown convention, rather than the extension's own $$…$$ and
// $$$…$$$: $x^2$ inline, $$…$$ on a line of its own for a displayed equation.
// A price is not an equation, so, as in Pandoc, the opening $ must be
// followed by a non-space and the closing $ must follow one: "$5 and $10"
// stays text.
export const INLINE_MATH = /(?<![$\\\w])\$([^\s$](?:[^$\n]*?[^\s$\\])?)\$$/;
export const BLOCK_MATH = /^\$\$([^$]+)\$\$$/;

const NoteInlineMath = InlineMath.extend({
  addInputRules() {
    return [
      new InputRule({
        find: INLINE_MATH,
        handler: ({ state, range, match }) => {
          state.tr.replaceWith(range.from, range.to, this.type.create({ latex: match[1] }));
        },
      }),
    ];
  },
});

const NoteBlockMath = BlockMath.extend({
  addInputRules() {
    return [
      new InputRule({
        find: BLOCK_MATH,
        handler: ({ state, range, match }) => {
          const $from = state.doc.resolve(range.from);
          const node = this.type.create({ latex: match[1].trim() });
          // Take the whole paragraph the $$…$$ was typed in, not just its
          // text, so no empty paragraph is left behind above the equation.
          const wholeBlock = $from.depth > 0
            && range.from === $from.start()
            && range.to === $from.end();
          if (wholeBlock) {
            state.tr.replaceWith($from.before(), $from.after(), node);
          } else {
            state.tr.replaceWith(range.from, range.to, node);
          }
        },
      }),
    ];
  },
});

export const noteMathExtensions = (
  getEditor: () => Editor | null,
  // Passed in from the component that owns the editor, which has the
  // translations; English is the fallback for callers without them.
  promptLabel: string = 'LaTeX',
) => [
  NoteInlineMath.configure({
    katexOptions: KATEX_OPTIONS,
    onClick: (node, pos) => {
      const editor = getEditor();
      if (editor) editLatex(editor, 'inlineMath', node.attrs.latex, pos, promptLabel);
    },
  }),
  NoteBlockMath.configure({
    katexOptions: { ...KATEX_OPTIONS, displayMode: true },
    onClick: (node, pos) => {
      const editor = getEditor();
      if (editor) editLatex(editor, 'blockMath', node.attrs.latex, pos, promptLabel);
    },
  }),
];

/** The toolbar's way in: ask for the source and drop the equation at the caret. */
export const insertInlineMath = (
  editor: Editor,
  promptLabel: string = 'LaTeX (e.g. E = mc^2)',
) => {
  const latex = window.prompt(promptLabel);
  if (!latex || !latex.trim()) return;
  editor.chain().focus().insertInlineMath({ latex: latex.trim() }).run();
};
